/* eslint-disable max-classes-per-file */
import _ from "lodash";
import API from "../API";
import { DateService, dateCompareFnFactoryFromKey, msInAnHour } from "./dates.service";
import { availableStates } from "../constants";

function addUTCSuffix(timestamp) {
  if (typeof timestamp === "string" && !(timestamp.endsWith("+00:00") || timestamp.endsWith("Z")))
    return `${timestamp}+00:00`;

  return timestamp;
}

/**
 * Normalizes an event location to a GeoJSON geometry. The API may send the location as a
 * geometry, a Feature wrapping a geometry, or either of those serialized as a JSON string.
 */
function parseLocation(location) {
  let parsed = location;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch (e) {
      return null;
    }
  }
  const geometry = _.get(parsed, "geometry") || parsed;
  return _.get(geometry, "coordinates.length") >= 2 ? geometry : null;
}

export class BaseSortableByStartTime {
  constructor(props) {
    Object.assign(this, props);

    this.timestamp = addUTCSuffix(props.timestamp);
    this.start_time = addUTCSuffix(props.start_time);
    this.end_time = this.end_time && addUTCSuffix(props.end_time);
    this.key = JSON.stringify(this);
  }

  /**
   * Returns true if the Status started before the event started
   */
  startedBefore = (event) => {
    const startTime = DateService.convert(this.timestamp).valueOf();
    const checkTime = DateService.convert(event.timestamp).valueOf();
    return startTime <= checkTime;
  };

  static compareTime = dateCompareFnFactoryFromKey("timestamp");

  static bucketByDate(sortables) {
    return _.groupBy(sortables, (item) => BaseSortableByStartTime.getDate(item.timestamp));
  }

  static getDate(time) {
    const timestamp = DateService.convert(time).valueOf();
    return DateService.roundEpochTimeToDate(timestamp);
  }
}

export class TimelineStatus extends BaseSortableByStartTime {
  constructor(props) {
    super(props);
    this.timelineEvents = props.timeline_events?.map((event) => new TimelineEvent(event)) || [];
  }

  getDuration() {
    if (!this.end_time) {
      return null;
    }
    try {
      const start = DateService.convert(this.start_time).valueOf();
      const stop = DateService.convert(this.end_time).valueOf();
      return stop - start;
    } catch (e) {
      console.error(`error in getDuration: ${e}`);
      return 0;
    }
  }

  displayDuration() {
    const duration = this.getDuration();
    return duration
      ? DateService.format(duration, duration >= msInAnHour ? "00h 00m" : "00m")
      : "00m";
  }

  displayDistance() {
    if (!this.distance) {
      return "0 m";
    }
    const distance = Number.parseFloat(this.distance * 1000).toFixed(0);
    return `${distance} m`;
  }

  displayAvgSpeed() {
    if (!this.distance || !this.getDuration()) {
      return "0 km/h";
    }
    const durationInHrs = this.getDuration() / msInAnHour;
    const speed = Number.parseFloat(this.distance / durationInHrs).toFixed(2);
    return `${speed} km/h`;
  }

  getTimelineEvents() {
    return this.timelineEvents.slice(0).sort(TimelineEvent.compareTime).reverse();
  }

  get state() {
    return availableStates[this.vehicle_state];
  }
}

export class TripTimelineStatus extends TimelineStatus {
  constructor(tripStatus) {
    super(tripStatus);

    this.startTimelineEvent = new TimelineEvent(
      {
        ...tripStatus,
        location: tripStatus.start_location,
      },
      "tripStart",
    );

    if (this.end_time)
      this.endTimelineEvent = new TimelineEvent(
        {
          ...tripStatus,
          timestamp: this.end_time,
          location: tripStatus.stop_location,
        },
        "tripEnd",
      );
  }

  isATrip = true;

  static getTripInfo(tripTimelineStatuses) {
    const tripsByDay = BaseSortableByStartTime.bucketByDate(tripTimelineStatuses);

    if (!Object.keys(tripsByDay).length) {
      return {
        totalTrips: 0,
        averageTripTime: 0,
        tripsPerDay: 0,
      };
    }

    const initObj = { totalTrips: 0, totalTripTime: 0, totalDays: 0 };

    const data = Object.values(tripsByDay).reduce((info, tripArrayForDay) => {
      info.totalTrips += tripArrayForDay.length;
      info.totalDays += 1;
      info.totalTripTime += tripArrayForDay.reduce((sum, trip) => sum + trip.getDuration(), 0);
      return info;
    }, initObj);
    return {
      totalTrips: data.totalTrips,
      averageTripTime: DateService.msToDateService(data.totalTripTime / data.totalTrips),
      tripsPerDay: Math.round((data.totalTrips / data.totalDays) * 10) / 10,
    };
  }

  getTimelineEvents() {
    const sortedEvents = [...super.getTimelineEvents(), this.startTimelineEvent];

    if (!this.endTimelineEvent)
      return sortedEvents;

    let newTimestamp = this.endTimelineEvent.timestamp;
    if (
      newTimestamp &&
      sortedEvents.length > 0 &&
      sortedEvents[0].timestamp.valueOf() > this.endTimelineEvent.timestamp.valueOf()
    ) {
      newTimestamp = sortedEvents[0].timestamp;
    }
    const endEvents = [
      new TimelineEvent(
        {
          ...this.endTimelineEvent,
          timestamp: newTimestamp,
        },
        "tripEnd",
        false,
      ),
    ];
    // add success "graphic" event at the end of a successful trip
    // we filter this out in TimelineStatusSummary.jsx
    if (sortedEvents.length > 0 && sortedEvents[0].rawLog.type !== "fault") {
      endEvents.unshift(
        new TimelineEvent({
          type: "timelineGraphic",
          id: 0,
          module: "vehicle",
          timestamp: newTimestamp,
        }),
      );
    }
    return [...endEvents, ...sortedEvents];
  }
}

export class TimelineEvent extends BaseSortableByStartTime {
  constructor(rawEvent, type) {
    super(rawEvent);

    this.type = type || rawEvent.type;
    this.rawLog = rawEvent;
    this.id = rawEvent.id;
    this.module = rawEvent.module;
    this.location = parseLocation(rawEvent.location);
  }

  toRawLog = async () => {
    if (this.rawLog) {
      return [this.rawLog];
    }
    return API.trips.getLogs(this.id);
  };

  static getTitle = (event) => {
    switch (event.type) {
      case "warning":
        return "Info";
      case "tripStart":
        return "Trip start";
      case "tripEnd":
        return "Trip completed";
      // special case for
      case "timelineGraphic":
        return "Info";
      default:
        return event.type;
    }
  };

  static getDescription = (eventsMap, event) => {
    if (event.module && event.type && !Number.isNaN(event.id)) {
      return _.get(eventsMap, [[event.type], [event.module], [event.id]]);
    }
    if (event.type) {
      return eventsMap[event.type].description;
    }
    return "";
  };

  getDescription = (eventsMap) => TimelineEvent.getDescription(eventsMap, this);
}
